import { RiAddCircleLine, RiAlertFill, RiArrowRightSLine } from 'react-icons/ri';
import { FC, useMemo } from 'react';
import { AddSamplePlanGroup } from '@/components/Samples/AddSamplePlanGroup';
import {
  Step,
  useAddSamplePlanContext,
  useCascadeUpdateWarning,
  useIsSubmitting,
  usePlannedSamples,
  usePlannedSampleTypes,
} from '@/components/Samples/AddSamplePlanProvider';
import Button from '@/components/UI/Button';
import { _notNil } from '@/littledash';
import { AssignAnimalsForm } from '@/components/Samples/AssignAnimals/AssignAnimalsForm';
import { AssignAnimalsProvider } from '@/components/Samples/AssignAnimals/AssignAnimalsProvider';
import { containsArchivedSampleTypes } from '@/components/Samples/Samples.utils';

const SampleTypeDefineForm: FC = () => {
  const { samplePlanState } = useAddSamplePlanContext();
  const plannedSampleTypes = usePlannedSampleTypes();
  return (
    <div data-test-component="SampleTypeDefineForm" data-test-element="container">
      {plannedSampleTypes.map((type) => (
        <AddSamplePlanGroup key={type} type={type} />
      ))}
      <div className="pv2" data-test-element="add-sample-type-button-container">
        <Button pill onClick={() => samplePlanState.createTempSample()} testId="add-sample-type">
          <span className="flex flex-row items-center">
            <RiAddCircleLine />
            <span className="pl1">Add another sample type</span>
          </span>
        </Button>
      </div>
    </div>
  );
};

const StepSelector: FC<{ step: Step }> = ({ step }) => {
  switch (step) {
    case 'sample_type_define':
      return <SampleTypeDefineForm />;
    case 'assign_animals':
      return (
        <AssignAnimalsProvider>
          <AssignAnimalsForm />
        </AssignAnimalsProvider>
      );
  }
  return null;
};

export const AddSamplePlansForm: FC = () => {
  const { stepInValid, sampleGlossary, step, nextStep, prevStep } = useAddSamplePlanContext();
  const plannedSamples = usePlannedSamples();
  const isSubmitting = useIsSubmitting();
  const affectedSampleCount = useCascadeUpdateWarning();

  const containsArchivedTypes = useMemo(
    () => containsArchivedSampleTypes(plannedSamples, sampleGlossary),
    [plannedSamples, sampleGlossary]
  );

  const isFinalStep = step === 'assign_animals';
  const isNextDisabled = stepInValid || containsArchivedTypes || isSubmitting;
  const nextButtonTooltip = containsArchivedTypes
    ? 'Cannot edit sample plan as it includes archived sample types'
    : stepInValid
      ? 'There are invalid cells'
      : undefined;

  return (
    <div className="flex flex-column" data-test-component="AddSamplePlansForm" data-test-element="container">
      <div className="flex flex-row justify-between mb2">
        <div className="bg-near-white flex flex-row items-center justify-start f6 pv2 ph3 ba b--gray">
          <div
            className={step === 'sample_type_define' ? 'fw5 black' : 'fw4'}
            data-testid="step-indicator-define-sample-types"
          >
            Define sample types
          </div>
          <RiArrowRightSLine className="mh4" />
          <div className={step === 'assign_animals' ? 'fw5 black' : 'fw4'} data-testid="step-indicator-assign-animals">
            Assign Animals
          </div>
        </div>
      </div>
      <StepSelector step={step} />
      <div className="flex flex-row justify-start gap-h-3 pv3 mt3 bt" data-test-element="form-actions">
        <Button
          primary
          disabled={isNextDisabled}
          tooltip={nextButtonTooltip}
          onClick={() => nextStep()}
          testId={isFinalStep ? 'submit-button' : 'next-button'}
          loading={isSubmitting}
        >
          {isFinalStep ? 'Submit' : 'Next'}
        </Button>
        {affectedSampleCount > 0 && (
          <div className="flex items-center mr3" data-testid="cascade-update-warning">
            <RiAlertFill className="yellow" size={20} />
            <span className="pl2 f6">
              This update will affect {affectedSampleCount} planned sample{affectedSampleCount > 1 ? 's' : ''}
            </span>
          </div>
        )}
        {_notNil(prevStep) ? (
          <Button plain onClick={() => prevStep()} disabled={isSubmitting} testId="back-button">
            Back
          </Button>
        ) : null}
      </div>
    </div>
  );
};
